import React, { useCallback, useEffect, useRef, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CPInput } from "../CPInput";
import { CPTextarea } from "../CPTextarea";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import "./adminDesktop.css";
import { supabase } from "../../supabase";
import { useMembership } from "../../context/MembershipContext";
import { useOrganization } from "../../context/OrganizationContext";
import {
  clearAdminOrganizationProfileDraft,
  organizationProfileFormToPatch,
  organizationProfileFromSettings,
  organizationProfilesEqual,
  ORG_PROFILE_FORM_DEFAULTS,
  resolveOrganizationProfileHydration,
  writeUserOrganizationProfileDraft,
} from "../../helpers/adminOrganizationProfileDraft";
import {
  ORGANIZATION_REGISTRATION_LABELS,
  ORGANIZATION_REGISTRATION_MAX_LENGTH,
  validateOrganizationRegistration,
} from "../../helpers/organizationRegistration";

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p className="admin-placeholder-note adm-error" role="alert" style={{ marginTop: "-8px" }}>
      {message}
    </p>
  );
}

export function AdminOrganizationScreen({ onBack }) {
  const { settings, updateSettings, loading, error } = useOrganization();
  const { organizationId, isOrgAdmin } = useMembership();
  const isDesktop = useIsDesktopViewport();
  const [joinCode, setJoinCode] = useState("");
  const [joinCodeEnabled, setJoinCodeEnabled] = useState(true);

  const [name, setName] = useState(ORG_PROFILE_FORM_DEFAULTS.name);
  const [legalName, setLegalName] = useState(ORG_PROFILE_FORM_DEFAULTS.legalName);
  const [address, setAddress] = useState(ORG_PROFILE_FORM_DEFAULTS.address);
  const [phone, setPhone] = useState(ORG_PROFILE_FORM_DEFAULTS.phone);
  const [email, setEmail] = useState(ORG_PROFILE_FORM_DEFAULTS.email);
  const [website, setWebsite] = useState(ORG_PROFILE_FORM_DEFAULTS.website);
  const [timezone, setTimezone] = useState(ORG_PROFILE_FORM_DEFAULTS.timezone);
  const [locale, setLocale] = useState(ORG_PROFILE_FORM_DEFAULTS.locale);
  const [currency, setCurrency] = useState(ORG_PROFILE_FORM_DEFAULTS.currency);
  const [businessNumber, setBusinessNumber] = useState(
    ORG_PROFILE_FORM_DEFAULTS.businessNumber,
  );
  const [gstHstRegistrationNumber, setGstHstRegistrationNumber] = useState(
    ORG_PROFILE_FORM_DEFAULTS.gstHstRegistrationNumber,
  );
  const [qstRegistrationNumber, setQstRegistrationNumber] = useState(
    ORG_PROFILE_FORM_DEFAULTS.qstRegistrationNumber,
  );
  const [additionalRegistrationLabel, setAdditionalRegistrationLabel] = useState(
    ORG_PROFILE_FORM_DEFAULTS.additionalRegistrationLabel,
  );
  const [additionalRegistrationNumber, setAdditionalRegistrationNumber] =
    useState(ORG_PROFILE_FORM_DEFAULTS.additionalRegistrationNumber);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const savedProfileRef = useRef(ORG_PROFILE_FORM_DEFAULTS);
  const userEditedRef = useRef(false);
  const formSnapshotRef = useRef(ORG_PROFILE_FORM_DEFAULTS);

  const formSnapshot = {
    name,
    legalName,
    address,
    phone,
    email,
    website,
    timezone,
    locale,
    currency,
    businessNumber,
    gstHstRegistrationNumber,
    qstRegistrationNumber,
    additionalRegistrationLabel,
    additionalRegistrationNumber,
  };

  formSnapshotRef.current = formSnapshot;

  const applyFormState = (form) => {
    setName(form.name);
    setLegalName(form.legalName);
    setAddress(form.address);
    setPhone(form.phone);
    setEmail(form.email);
    setWebsite(form.website);
    setTimezone(form.timezone);
    setLocale(form.locale);
    setCurrency(form.currency);
    setBusinessNumber(form.businessNumber || "");
    setGstHstRegistrationNumber(form.gstHstRegistrationNumber || "");
    setQstRegistrationNumber(form.qstRegistrationNumber || "");
    setAdditionalRegistrationLabel(form.additionalRegistrationLabel || "");
    setAdditionalRegistrationNumber(form.additionalRegistrationNumber || "");
  };

  const markUserEdited = useCallback(() => {
    userEditedRef.current = true;
  }, []);

  const persistDraftIfDirty = useCallback(() => {
    if (!organizationId || !userEditedRef.current) {
      return;
    }

    const form = formSnapshotRef.current;
    if (organizationProfilesEqual(form, savedProfileRef.current)) {
      clearAdminOrganizationProfileDraft(organizationId);
      userEditedRef.current = false;
      return;
    }

    writeUserOrganizationProfileDraft(organizationId, form);
  }, [organizationId]);

  useEffect(() => {
    userEditedRef.current = false;
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) {
      return;
    }

    if (userEditedRef.current) {
      savedProfileRef.current = organizationProfileFromSettings(settings);
      return;
    }

    const hydration = resolveOrganizationProfileHydration(settings, organizationId);
    savedProfileRef.current = hydration.savedProfile;
    applyFormState(hydration.formProfile);
    userEditedRef.current = hydration.restoreUserDraft;

    if (!hydration.restoreUserDraft) {
      clearAdminOrganizationProfileDraft(organizationId);
    }
  }, [organizationId, settings]);

  useEffect(() => {
    if (!organizationId || !userEditedRef.current) {
      return;
    }

    persistDraftIfDirty();
  }, [
    organizationId,
    persistDraftIfDirty,
    name,
    legalName,
    address,
    phone,
    email,
    website,
    timezone,
    locale,
    currency,
    businessNumber,
    gstHstRegistrationNumber,
    qstRegistrationNumber,
    additionalRegistrationLabel,
    additionalRegistrationNumber,
  ]);

  useEffect(() => {
    if (!organizationId) {
      return undefined;
    }

    const handleBackground = () => {
      if (document.visibilityState === "hidden") {
        persistDraftIfDirty();
      }
    };

    document.addEventListener("visibilitychange", handleBackground);

    return () => {
      document.removeEventListener("visibilitychange", handleBackground);
      persistDraftIfDirty();
    };
  }, [organizationId, persistDraftIfDirty]);

  useEffect(() => {
    if (!organizationId || !isOrgAdmin) {
      return;
    }

    supabase
      .from("organizations")
      .select("join_code, join_code_enabled")
      .eq("id", organizationId)
      .maybeSingle()
      .then(({ data, error: joinError }) => {
        if (joinError) {
          console.warn("[AdminOrganization] join code load failed", joinError);
          return;
        }
        setJoinCode(data?.join_code || "");
        setJoinCodeEnabled(data?.join_code_enabled !== false);
      });
  }, [organizationId, isOrgAdmin]);

  const handleSave = async () => {
    const validation = validateOrganizationRegistration(formSnapshotRef.current);
    if (!validation.valid) {
      setFieldErrors(validation.errors);
      return;
    }

    setFieldErrors({});
    setSaving(true);
    try {
      const nextSettings = await updateSettings({
        organization: organizationProfileFormToPatch(formSnapshotRef.current),
      });
      clearAdminOrganizationProfileDraft(organizationId);
      userEditedRef.current = false;

      const savedProfile = organizationProfileFromSettings(nextSettings);
      savedProfileRef.current = savedProfile;
      applyFormState(savedProfile);

      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const saveControls = (
    <>
      <button
        type="button"
        className="kpi-back-btn adm-primary"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? "Saving…" : "Save organization profile"}
      </button>

      {saved && (
        <p className="admin-save-confirmation" role="status">
          Organization profile saved.
        </p>
      )}
    </>
  );

  return (
    <CrewPilotModuleShell
      title="Organization"
      subtitle="Company profile and regional defaults"
      sectionLabel="Company"
      onBack={onBack}
      layout={PAGE_LAYOUT.FORM}
    >
      <div className="adm-split">
      <div className="rb-card">
        <div className="section-heading">COMPANY PROFILE</div>

        {loading && (
          <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
            Loading organization profile…
          </p>
        )}

        {error && (
          <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
            Unable to reach the database. Showing cached settings; changes are
            saved locally until sync is available.
          </p>
        )}

        <div className="adm-fields">
        <CPInput
          label="Company name"
          value={name}
          onChange={(next) => {
            markUserEdited();
            setName(next);
          }}
        />

        <CPInput
          label="Legal name"
          value={legalName}
          onChange={(next) => {
            markUserEdited();
            setLegalName(next);
          }}
        />

        <CPTextarea
          className="adm-wide"
          label="Address"
          rows={3}
          value={address}
          onChange={(next) => {
            markUserEdited();
            setAddress(next);
          }}
        />

        <CPInput
          label="Phone"
          type="tel"
          value={phone}
          onChange={(next) => {
            markUserEdited();
            setPhone(next);
          }}
        />

        <CPInput
          label="Email"
          type="email"
          value={email}
          onChange={(next) => {
            markUserEdited();
            setEmail(next);
          }}
        />

        <CPInput
          label="Website"
          value={website}
          onChange={(next) => {
            markUserEdited();
            setWebsite(next);
          }}
        />

        <CPInput
          label="Timezone"
          value={timezone}
          onChange={(next) => {
            markUserEdited();
            setTimezone(next);
          }}
        />

        <CPInput
          label="Locale"
          value={locale}
          onChange={(next) => {
            markUserEdited();
            setLocale(next);
          }}
        />

        <CPInput
          label="Currency"
          value={currency}
          onChange={(next) => {
            markUserEdited();
            setCurrency(next);
          }}
        />
        </div>
      </div>

      <div className="adm-stack">
      <div className="rb-card" style={{ marginTop: "16px" }}>
        <div className="section-heading">BUSINESS REGISTRATION</div>
        <p className="admin-placeholder-note" style={{ marginBottom: "16px" }}>
          Optional identification numbers shown on invoices when filled in.
          These fields do not enable taxes or change invoice calculations.
        </p>

        <div className="adm-fields">
        <CPInput
          label={ORGANIZATION_REGISTRATION_LABELS.businessNumber}
          value={businessNumber}
          maxLength={ORGANIZATION_REGISTRATION_MAX_LENGTH}
          onChange={(next) => {
            markUserEdited();
            setBusinessNumber(next);
          }}
        />
        <FieldError message={fieldErrors.businessNumber} />

        <CPInput
          label={ORGANIZATION_REGISTRATION_LABELS.gstHstRegistrationNumber}
          value={gstHstRegistrationNumber}
          maxLength={ORGANIZATION_REGISTRATION_MAX_LENGTH}
          onChange={(next) => {
            markUserEdited();
            setGstHstRegistrationNumber(next);
          }}
        />
        <FieldError message={fieldErrors.gstHstRegistrationNumber} />

        <CPInput
          label={ORGANIZATION_REGISTRATION_LABELS.qstRegistrationNumber}
          value={qstRegistrationNumber}
          maxLength={ORGANIZATION_REGISTRATION_MAX_LENGTH}
          onChange={(next) => {
            markUserEdited();
            setQstRegistrationNumber(next);
          }}
        />
        <FieldError message={fieldErrors.qstRegistrationNumber} />

        <CPInput
          label="Additional registration label"
          placeholder="PST Registration Number"
          value={additionalRegistrationLabel}
          maxLength={ORGANIZATION_REGISTRATION_MAX_LENGTH}
          onChange={(next) => {
            markUserEdited();
            setAdditionalRegistrationLabel(next);
          }}
        />
        <FieldError message={fieldErrors.additionalRegistrationLabel} />

        <CPInput
          label="Additional registration number"
          value={additionalRegistrationNumber}
          maxLength={ORGANIZATION_REGISTRATION_MAX_LENGTH}
          onChange={(next) => {
            markUserEdited();
            setAdditionalRegistrationNumber(next);
          }}
        />
        <FieldError message={fieldErrors.additionalRegistrationNumber} />
        </div>

        {!isDesktop && saveControls}
      </div>

      {isOrgAdmin && joinCode && (
        <div className="rb-card" style={{ marginTop: "16px" }}>
          <div className="section-heading">EMPLOYEE JOIN CODE</div>
          <p className="profile-value adm-code" style={{ letterSpacing: "0.12em" }}>
            {joinCode}
          </p>
          <p className="admin-placeholder-note">
            {joinCodeEnabled
              ? "Employees can join using Administration → Join organization."
              : "Join code is currently disabled for this organization."}
          </p>
        </div>
      )}
      </div>

      {isDesktop && (
        <div className="adm-actions adm-actions--bar">{saveControls}</div>
      )}
      </div>
    </CrewPilotModuleShell>
  );
}
